import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    StepList,
    FAQList,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';
import sharePdfDrawingsCopy from './share-pdf-drawings-for-review.content.json';

const PAGE = pageForRoute('/redline-pdf-drawing');

// Shipped: local PDF opening (src/features/local/openLocalPdf.ts), clouds, callouts, and text
// (src/core/geometry/markup-config.ts), dimensions
// (src/components/features/viewer/markup-renderers/DimensionRenderer.tsx), markup list and CSV
// (src/components/features/viewer/ViewerMarkupsSidebar.tsx, src/components/features/markups/useMarkupExport.ts),
// PDF export with optional-content groups (src/core/pdf-export/ocg-writer.ts), and view-only share links
// (src/components/features/collaboration/ShareLinkDialog.tsx). Photo attachments are implemented in
// (src/core/image/mediaRequest.ts, src/components/features/viewer/media/CloudPhotos.tsx).
const STEPS = [
    {
        title: '1. Open the drawing',
        body: 'Choose Open a PDF drawing and select the sheet from your device. It opens in the browser for review. Start with the sheet number and title block so your comment can point back to a location the architect can identify. If the detail you need is on another sheet, note its sheet and detail reference before adding the markup.',
        image: '/tool-shots/cloud.jpg',
        alt: 'A construction plan with a revision cloud around a bathroom fixture',
    },
    {
        title: '2. Draw a revision cloud around the issue',
        body: 'Choose the cloud tool and draw around the specific area that needs a change. Keep the cloud tight enough to distinguish the issue from nearby work. A separate cloud for each issue makes the review easier to scan and gives each instruction a clear location. You can select the cloud later to adjust its appearance or remove it.',
        image: '/tool-shots/cloud.jpg',
        alt: 'Revision cloud marking one location on a plan sheet',
    },
    {
        title: '3. Add a callout or text instruction',
        body: 'Use a callout when you want the note visibly connected to the cloud, or place a text note nearby. Say what should change, where it should happen, and which detail or sheet gives the context. For example, name the item and the action instead of writing only "fix this." Keep the sentence short enough to read at the drawing scale.',
        image: '/tool-shots/callout.jpg',
        alt: 'A callout connected to a construction drawing location',
    },
    {
        title: '4. Add a dimension when size matters',
        body: 'If your comment proposes a size or clearance, add a dimension to show the intended measurement. Calibrate the drawing against a known length first, then place the dimension between the points that define the requested change. The dimension tool reports a scaled distance after calibration. Skip this step when a written instruction is enough or the drawing scale is not known.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A dimension line with a measurement on a plan drawing',
    },
    {
        title: '5. Review the markup list',
        body: 'Open the Markups list and read each entry as if you had not drawn it. Confirm that the note identifies the action and location, then check that the cloud, callout, and dimension appear on the intended sheet. Remove duplicates and clarify notes that depend on context only you know. The list can also be exported as a CSV file for sorting or tracking outside the PDF.',
        image: '/tool-shots/text.jpg',
        alt: 'A text annotation placed on a marked-up drawing',
    },
    {
        title: '6. Export an annotated PDF and the CSV markup list',
        body: 'Export the annotated PDF when the visual comments are ready to return. The export includes the drawing and its markups; when the drawing has layers, the PDF export can preserve markup layers as optional content groups. Export the CSV markup list separately if the recipient also needs a row-based record of the comments. Open the exported files once to make sure the expected sheet and notes are present.',
        image: '/tool-shots/export-dialog.png',
        alt: 'PDF Markup Tool export dialog with page range and editable or flattened markup options',
    },
    {
        title: '7. Send the files back',
        body: 'Attach the annotated PDF to your email or project message and identify the drawing revision in your note. Include the CSV when the recipient will track comments in a spreadsheet. In the message, call out any time-sensitive item and explain whether the file is for review or for incorporation. Keep the original source file and your exported copy easy to tell apart.',
        image: '/tool-shots/markup-export-menu.png',
        alt: 'The markups list export menu with the Export CSV option',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Do I need an account to redline a PDF drawing?',
        answer: ['No account is needed to open a PDF locally, add markups, and export the annotated PDF.'],
    },
    {
        question: 'Is my drawing uploaded when I open it?',
        answer: ['For the local workflow on this page, the PDF is opened in your browser from your device. The local PDF markup workflow does not upload the drawing.'],
    },
    {
        question: 'Can the recipient open the marked-up PDF without PDF Markup Tool?',
        answer: ['Yes. Send the exported PDF file as an attachment; the recipient can open that PDF in a PDF reader. The CSV markup list is a separate optional file.'],
    },
    {
        question: 'Can I add photos to a markup?',
        answer: ['Yes. Add a photo to a markup in the tool, then export the annotated PDF to send the review record with the drawing.'],
    },
] as const;

export function RedlinePdfDrawingPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="PDF drawing review"
                    title="How to redline a PDF drawing and send comments back"
                    lead="Open a PDF in your browser, cloud and callout the issue, export an annotated PDF, and send it back to the architect."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF drawing</Button>}
                    chips={['No upload for local PDFs', 'No account needed', 'PDF and CSV export']}
                />
                <StepList
                    title="How to redline a PDF drawing"
                    lead="Follow these steps to mark a sheet, make each instruction clear, and return a file the next person can review."
                    steps={STEPS}
                />
                <section className="mktg-features">
                    <div className="mktg-container">
                        <h2 className="mktg-section-title">Write comments the architect can resolve</h2>
                        <p className="mktg-section-lead">A useful redline gives the next person enough information to locate the issue and understand the requested action.</p>
                        <p>Keep one issue inside each cloud so a reviewer can refer to a specific location. Write the change you want, not only a reaction such as "wrong" or "please check." Name the item, the action, and any condition that affects the decision. If a note points to another view, give the sheet number and detail reference so the reader can find it without searching the whole set.</p>
                        <p>Place the instruction close to the cloud, and use a callout leader when the connection might otherwise be unclear. If the requested change depends on a size, show a dimension only after checking the drawing scale. Do not use an uncalibrated measurement as a promise of real-world size. Before exporting, read the note beside the drawing and ask whether someone who did not attend the review could understand what to do next.</p>
                    </div>
                </section>
                {/* Shipped: PDF export and optional-content groups (src/core/pdf-export/export-document.ts, src/core/pdf-export/ocg-writer.ts); CSV export (src/components/features/markups/useMarkupExport.ts). */}
                <section className="mktg-context">
                    <div className="mktg-container">
                        <h2 className="mktg-section-title">What the architect receives</h2>
                        <p>The annotated PDF keeps the drawing and the visible review markups together in a file that can be attached to a message. PDF Markup Tool can preserve markup layers as optional content groups in the exported PDF, so a compatible reader can show or hide those groups. The marks remain visible in the exported drawing for a straightforward review.</p>
                        <p>The CSV markup list is a separate export for recipients who want a row-based list of comments. Include it when it helps the team sort or track issues; the annotated PDF remains the visual record that shows each comment in place on its sheet. You can send the PDF alone when that is all the recipient needs.</p>
                    </div>
                </section>
                <ContextNote text="This walkthrough describes the local PDF workflow. Cloud review sessions are separate and are not required to export and send an annotated PDF." />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Open a PDF drawing and start a redline"
                    lead="Mark the issue, make the instruction clear, and export a copy to send back."
                    actions={<><Button href="/" variant="primary" size="lg">Open a PDF drawing</Button><Button href="/share-pdf-drawings-for-review/" variant="ghost" size="lg">{sharePdfDrawingsCopy.relatedLinkLabel}</Button><Button href="/ipad-pdf-markup/" variant="ghost" size="lg">PDF markup on iPad</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Free browser-native PDF markup for construction drawings" />
        </MarketingPage>
    );
}
